import { orderBy } from 'lodash-es'
import pluralize from 'pluralize'
import { useState } from 'react'

import type { SliderLayerProps } from '@fv/client-components'
import {
  type ComparableLoadSummary,
  type ComparableLoadsLaneToleranceRadius,
  type ComparableLoadsOptions,
  type ComparableLoadsWindow,
  comparableLoadsLaneToleranceRadii,
  comparableLoadsWindows,
  getComparableLoadsWindowLabel,
} from '@fv/models/admin'
import { capitalizeFirstLetter, formatMoney } from '@fv/models/core'

import { AdminSliderPanel } from '@/components/AdminSliderPanel'
import InfoBox from '@/components/InfoBox'
import { AdminButton } from '@/components/shared/AdminButton'
import { AdminDate } from '@/components/shared/AdminDate'
import { AdminFormGroup } from '@/components/shared/AdminFormGroup'
import { AdminLoading, QuerySuspense } from '@/components/shared/AdminLoading'
import { AdminSection } from '@/components/shared/AdminSection'
import { AdminSelect } from '@/components/shared/AdminSelect'
import { AdminTable } from '@/components/shared/AdminTable'
import { AdminToggle } from '@/components/shared/AdminToggle'

import { useComparableLoads } from './api'
import { ComparableLoadLink } from './LoadPreviewTooltip'
import type { ComparableLoadsResult } from './types'

type Props = {
  loadId: string
} & Pick<SliderLayerProps, 'isOpen' | 'onClose'>

type ComparableLoadsWindowQuery = Required<
  Pick<
    ComparableLoadsOptions,
    'bookedWindow' | 'pickupWindow' | 'laneToleranceRadiusMiles'
  >
>

const DEFAULT_QUERY: ComparableLoadsWindowQuery = {
  bookedWindow: '30-days',
  pickupWindow: '30-days',
  laneToleranceRadiusMiles: 25,
}

const windowSelectOptions = comparableLoadsWindows.map(value => ({
  value,
  text: getComparableLoadsWindowLabel(value),
}))

const laneToleranceRadiusSelectOptions = comparableLoadsLaneToleranceRadii.map(
  value => ({
    value: String(value),
    text: `${value} miles`,
  }),
)

const formatRate = (amount: number | null) =>
  amount != null ? formatMoney(amount, 'usd') : '—'

const formatSampleRate = (load: ComparableLoadSummary) =>
  formatRate(load.amount)

const formatSampleStatus = (status: ComparableLoadSummary['status']) =>
  capitalizeFirstLetter(status)

const RateStat = ({
  label,
  cwt,
  rate,
  count,
}: {
  label: string
  cwt: number | null
  rate: number | null
  count?: number
}) => (
  <div className="text-center">
    <div className="text-sm font-medium">{label}</div>
    <div className="text-2xl font-bold">
      {formatRate(cwt)} <span className="text-xs opacity-70">/cwt</span>
    </div>
    {rate != null && (
      <div className="text-sm opacity-70">{formatRate(rate)} avg rate</div>
    )}
    {count != null && (
      <div className="text-xs opacity-70">
        {count} comparable {pluralize('load', count)}
      </div>
    )}
  </div>
)

const ComparableLoadsContent = ({ data }: { data: ComparableLoadsResult }) => {
  const [includeAccountLoads, setIncludeAccountLoads] = useState(true)
  const allSamples = data.samples ?? []
  const samples = includeAccountLoads
    ? allSamples
    : allSamples.filter(sample => sample.accountId !== data.source.accountId)

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap justify-around gap-4 rounded-sm border border-base-300 p-4">
        <RateStat label="This load" cwt={data.source.cwt} rate={null} />
        <RateStat
          label="Market Rate"
          cwt={data.market.cwtAverage}
          rate={data.market.average}
          count={data.market.count}
        />
        <RateStat
          label="Account Rate"
          cwt={data.account.cwtAverage}
          rate={data.account.average}
          count={data.account.count}
        />
      </div>
      <p className="text-center text-xs opacity-70">
        Market and account rates are based on booked loads only.
      </p>

      <AdminSection
        title="Loads"
        headerActions={
          <div className="ml-auto">
            <AdminToggle
              className="toggle-xs"
              label="Include account loads"
              checked={includeAccountLoads}
              onChange={event => setIncludeAccountLoads(event.target.checked)}
            />
          </div>
        }
      >
        {samples.length ? (
          <AdminTable
            data={samples}
            rowKey={load => load.loadId}
            columns={[
              {
                key: 'loadId',
                label: 'Load',
                sortable: false,
                render: load => <ComparableLoadLink loadId={load.loadId} />,
              },
              {
                key: 'status',
                label: 'Status',
                sortable: false,
                render: load => formatSampleStatus(load.status),
              },
              {
                key: 'rate',
                label: 'Rate',
                sort: (data, dir) =>
                  orderBy(
                    data,
                    load => load.amount ?? load.cheapestQuoteAmount,
                    [dir],
                  ),
                render: load => formatSampleRate(load),
              },
              {
                key: 'pickupDate',
                label: 'Pickup',
                render: load => (
                  <AdminDate val={load.pickupDate} format="MM/DD/YYYY" />
                ),
              },
              {
                key: 'bookedDate',
                label: 'Booked',
                render: load =>
                  load.bookedDate ? (
                    <AdminDate val={load.bookedDate} format="MM/DD/YYYY" />
                  ) : (
                    '—'
                  ),
              },
            ]}
          />
        ) : (
          <InfoBox>No comparable loads found.</InfoBox>
        )}
      </AdminSection>
    </div>
  )
}

export const ComparableLoadsPanel = ({ loadId, ...props }: Props) => {
  const [query, setQuery] = useState<ComparableLoadsWindowQuery>(DEFAULT_QUERY)
  const { data, isLoading } = useComparableLoads(loadId, query, {
    enabled: props.isOpen,
  })

  return (
    <AdminSliderPanel title="Comparable Loads" width="60rem" {...props}>
      <div className="mb-4 gap-4 grid grid-cols-3">
        <AdminFormGroup label="Booked window">
          <AdminSelect
            className="select-sm"
            name="bookedWindow"
            options={windowSelectOptions}
            value={query.bookedWindow}
            onChange={event =>
              setQuery(current => ({
                ...current,
                bookedWindow: event.target.value as ComparableLoadsWindow,
              }))
            }
          />
        </AdminFormGroup>
        <AdminFormGroup label="Pickup window">
          <AdminSelect
            className="select-sm"
            name="pickupWindow"
            options={windowSelectOptions}
            value={query.pickupWindow}
            onChange={event =>
              setQuery(current => ({
                ...current,
                pickupWindow: event.target.value as ComparableLoadsWindow,
              }))
            }
          />
        </AdminFormGroup>
        <AdminFormGroup label="Lane tolerance radius">
          <AdminSelect
            className="select-sm"
            name="laneToleranceRadiusMiles"
            options={laneToleranceRadiusSelectOptions}
            value={String(query.laneToleranceRadiusMiles)}
            onChange={event =>
              setQuery(current => ({
                ...current,
                laneToleranceRadiusMiles: Number(
                  event.target.value,
                ) as ComparableLoadsLaneToleranceRadius,
              }))
            }
          />
        </AdminFormGroup>
      </div>
      <QuerySuspense query={{ isLoading }}>
        {data ? <ComparableLoadsContent data={data} /> : <AdminLoading />}
      </QuerySuspense>
    </AdminSliderPanel>
  )
}

export const ComparableLoadsButton = ({ loadId }: { loadId: string }) => {
  const [isOpen, setIsOpen] = useState(false)
  return (
    <>
      <AdminButton
        icon="rocket"
        className="btn-xs"
        onClick={() => setIsOpen(!isOpen)}
      >
        Comparable Loads
      </AdminButton>
      <ComparableLoadsPanel
        loadId={loadId}
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
      />
    </>
  )
}
